import { ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { useBackgroundSync } from '@/context/BackgroundSyncContext';
import NavigationSelect from '@/common/NavigationSelect';
import ConnectivityIndicator from '@/common/ConnectivityIndicator';
import SyncIndicator from '@/common/SyncIndicator/SyncIndicator';
import { getNavigationSchema, DropdownOptions } from '@/routes/navigation';
import useOnlineStatus from '@/hooks/useOnlineStatus';
import { getUser } from '@/library/UserSessionHelper';
import { isIOSWebView, isMobileResolution, isProductionEnv, getEnvLabel } from '@/utils';
import { tourReportName } from '@/constants';
import MenuIcon from '@/assets/icons/menu.svg?react';
import SidebarMenu from '@/layout/TourBagNavbar/SidebarMenu';
import styles from './TourBagNavbar.module.scss';

// TODO: If reusing for a different module like Order with other reports, refactor to use the pathname to decide which reports to check visibility for
export const isReportVisible = (reportLabel: string, reportSettings: Record<string, boolean>): boolean => {
  switch (reportLabel) {
    case tourReportName.bsgReport:
      return reportSettings.bsgReport;
    case tourReportName.tourBagReport:
      return reportSettings.tourBagReport;
    case tourReportName.tourPlayerReport:
      return reportSettings.tourPlayerReport;
    case tourReportName.marketingReport:
      return reportSettings.marketingReport;
    case tourReportName.contractReport:
      return reportSettings.contractReport;
    case tourReportName.contractHistoryReport:
      return reportSettings.contractHistoryReport;
    case tourReportName.transferClubReport:
      return reportSettings.transferClubReport;
    default:
      return false; // unknown reports are hidden
  }
};

interface TourBagNavbarProps {
  slot?: ReactNode;
}

export function TourBagNavbar({ slot }: TourBagNavbarProps) {
  const { pathname } = useLocation();
  const isOnline = useOnlineStatus();
  const { lastSyncDate } = useBackgroundSync();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState<DropdownOptions>([]);
  const schema = getNavigationSchema(pathname);
  const title = schema?.label || 'Tour App';
  const dropdownOptions = schema?.dropdownOptions || [];
  const dropdownPlaceholder = schema?.dropdownPlaceholder || 'Reports';
  const showReportsDropdown = !loading && Boolean(reports.length) && !isIOSWebView && !isMobileResolution;

  useEffect(() => {
    const fetchReportSettings = async () => {
      const user = await getUser();
      const tourReportSettings = user?.tourReportSettings as Record<string, boolean>;

      if (!tourReportSettings) {
        setLoading(false);
        return;
      }

      const filteredOptions = dropdownOptions.filter((option) => isReportVisible(option.label, tourReportSettings));
      setReports(filteredOptions);
      setLoading(false);
    };
    fetchReportSettings();
  }, []);

  return (
    <header className={styles.tourBagNavbar}>
      {/* use id="btnTouringNavMain" to match the legacy Vue app for test automation purposes */}
      <div id="btnTouringNavMain" className={styles.menuWrapper} onClick={() => setIsMenuOpen(!isMenuOpen)} role="button">
        <MenuIcon />
        <h1 className={styles.pageTitle}>{title} </h1>
        <span className={styles.onlineStatus}>
          <ConnectivityIndicator /> {!isOnline && <div className={styles.lastSync}>Last sync: {dayjs(lastSyncDate).format('MM/DD HH:mm')}</div>}
        </span>
        <SyncIndicator className={styles.syncIndicator} />
      </div>
      {!isProductionEnv && (
        <div className={`${styles.envLabel} ${isOnline ? styles.envLabelOnline : styles.envLabelOffline}`}>{`${getEnvLabel()?.toUpperCase()} ${isMobileResolution ? '' : 'ENV'}`}</div>
      )}
      {slot && <div className={styles.slot}>{slot}</div>}
      {isOnline && showReportsDropdown && <NavigationSelect placeholder={dropdownPlaceholder} options={reports} className={styles.select} />}
      <SidebarMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
    </header>
  );
}
